import { jwtDecode, JwtPayload } from "jwt-decode"
import { AuthError, UserJwt } from "./types"
import { safeError } from "@/utils/safeError"

export const getExpiresAtSeconds = (expiresAt: number): number => {
  const seconds = expiresAt - Date.now() / 1000
  return Math.round(seconds)
}

// Helper to extract user-friendly validation error message
const extractValidationMessage = (error: any): string => {
  // Handle FastAPI/Pydantic validation errors
  if (error.detail && Array.isArray(error.detail)) {
    const firstError = error.detail[0]
    if (firstError?.msg) {
      // Extract the human-readable part from messages like:
      // "value is not a valid email address: The part after the @-sign is not valid. It should have a period."
      const msg = firstError.msg
      if (msg.includes(":")) {
        const parts = msg.split(":")
        return parts[parts.length - 1].trim()
      }
      return msg
    }
  }

  // Fallback to general validation error message
  return "Please check your input and try again."
}

// Helper to convert API errors to AuthError
export const mapApiErrorToAuthError = (error: any): AuthError => {
  if (!error) {
    console.log("Unknown error:", safeError(error))
    return {
      type: "unknown",
      message: "An unknown error occurred",
      retryable: false,
    }
  }

  // Network/connection errors
  if (error.code === "NETWORK_ERROR" || error.name === "NetworkError") {
    return {
      type: "network",
      message: "Network connection failed",
      retryable: true,
    }
  }

  // Check for validation errors first (before checking status)
  // This handles cases where error.detail exists but status might not
  if (error.detail && Array.isArray(error.detail)) {
    const validationMessage = extractValidationMessage(error)
    return {
      type: "validation_error",
      message: validationMessage,
      retryable: false,
    }
  }

  // Handle specific error messages from detail property
  if (error.detail && typeof error.detail === "string") {
    const detail = error.detail.toLowerCase()

    // TOTP-specific errors
    if (detail.includes("invalid totp code")) {
      return {
        type: "invalid_totp",
        message: "Invalid verification code. Please try again.",
        retryable: false,
      }
    }
    if (detail.includes("totp is not enabled")) {
      return {
        type: "totp_not_enabled",
        message: "Two-factor authentication is not enabled",
        retryable: false,
      }
    }

    // Credential errors
    if (detail.includes("incorrect email or password")) {
      return {
        type: "invalid_credentials",
        message: "Invalid email or password",
        retryable: false,
      }
    }

    // Email verification
    if (
      detail.includes("email not verified") ||
      detail.includes("verify your email")
    ) {
      return {
        type: "email_not_verified",
        message: "Please verify your email address",
        retryable: false,
      }
    }

    // Email already registered
    if (detail.includes("email already registered")) {
      return {
        type: "email_already_registered",
        message:
          "This email is already registered. Please try signing in instead.",
        retryable: false,
      }
    }

    // Email validation rate limiting
    if (detail.includes("too many email validation attempts")) {
      return {
        type: "email_validation_rate_limited",
        message:
          "Too many email validation attempts. Please try again in an hour.",
        retryable: true,
      }
    }
  }

  // HTTP status-based errors (if status is available)
  if (error.status) {
    switch (error.status) {
      case 400:
        // Check for specific 400 error types based on detail
        if (error.detail && typeof error.detail === "string") {
          const detail = error.detail.toLowerCase()

          if (detail.includes("invalid totp code")) {
            return {
              type: "invalid_totp",
              message: "Invalid verification code. Please try again.",
              retryable: false,
            }
          }
          if (detail.includes("totp")) {
            return {
              type: "totp_required",
              message: "Two-factor authentication required",
              retryable: false,
            }
          }
          if (detail.includes("email already registered")) {
            return {
              type: "email_already_registered",
              message:
                "This email is already registered. Please try signing in instead.",
              retryable: false,
            }
          }
          if (detail.includes("email")) {
            return {
              type: "email_not_verified",
              message: "Please verify your email address",
              retryable: false,
            }
          }
        }
        return {
          type: "invalid_request",
          message: error.detail || "Bad request",
          retryable: false,
        }

      case 422:
        // Validation errors - extract meaningful message
        const validationMessage = extractValidationMessage(error)
        return {
          type: "validation_error",
          message: validationMessage,
          retryable: false,
        }

      case 429:
        // Check for specific rate limiting types
        if (error.detail && typeof error.detail === "string") {
          const detail = error.detail.toLowerCase()
          if (detail.includes("email validation")) {
            return {
              type: "email_validation_rate_limited",
              message:
                "Too many email validation attempts. Please try again in an hour.",
              retryable: true,
            }
          }
        }
        return {
          type: "rate_limited",
          message: "Too many attempts. Please try again later.",
          retryable: true,
        }

      case 500:
      case 502:
      case 503:
        return {
          type: "server_error",
          message: "Server temporarily unavailable",
          retryable: true,
        }
    }
  }

  // Fallback - use whatever message we can extract
  const message =
    error.detail || error.message || "An unexpected error occurred"
  return {
    type: "unknown",
    message:
      typeof message === "string" ? message : "An unexpected error occurred",
    retryable: false,
  }
}

export const decodeJWT = (token: string) => {
  return jwtDecode(token) as JwtPayload & {
    user_data: UserJwt
  }
}
